/**
 * Multi-File PDF Upload Dropzone Component
 * 
 * A specialized drag & drop file upload component for multiple PDF attachments.
 * Built on top of the existing FileUploadDropzone patterns but optimized for PDFs.
 * 
 * Features:
 * - Multiple PDF file upload
 * - Drag and drop support
 * - File validation (PDF only, max 10MB, max 100 pages)
 * - Progress indication
 * - Modern, clean UI
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import classnames from 'classnames/bind';
import { FileWithPath, useDropzone } from 'react-dropzone';

import COLOR from '@CONSTANTS/COLOR.constant';
import DESIGN from '@CONSTANTS/DESIGN.constant';

import styles from './MultiFilePDFDropzone.module.scss';

const cx: CX = classnames.bind(styles);

const baseStyle = {
    cursor: 'pointer',
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    padding: '32px 24px',
    borderWidth: 2,
    borderRadius: DESIGN['main-border-radius'],
    borderColor: COLOR['marriott-light-grey'],
    backgroundColor: COLOR['marriott-light-grey-2'],
    borderStyle: 'dashed',
    outline: 'none',
    transition: 'all 0.3s ease',
    minHeight: '200px',
    justifyContent: 'center',
};

const focusedStyle = {
    borderColor: COLOR['marriott-primary'],
    backgroundColor: '#f8fafc',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0, 164, 186, 0.15)',
};

const acceptStyle = {
    borderColor: '#00e676',
    backgroundColor: '#f0fff4',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0, 230, 118, 0.15)',
};

const rejectStyle = {
    borderColor: COLOR['marriott-orange'],
    backgroundColor: '#fef2f2',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 15px rgba(255, 107, 107, 0.15)',
};

// Removed FileWithPreview interface since we're preserving original File objects

interface MultiFilePDFDropzoneProps {
    onFilesSelected: (files: File[]) => void;
    disabled?: boolean;
    maxFiles?: number;
    helpText?: string;
}

function MultiFilePDFDropzone(props: MultiFilePDFDropzoneProps) {
    const {
        onFilesSelected,
        disabled = false,
        maxFiles = 10,
        helpText,
    } = props;

    // Removed uploadProgress state as it's not currently used

    // Validate PDF file
    const validatePDFFile = (file: File): string[] => {
        const errors: string[] = [];
        
        if (file.type !== 'application/pdf') {
            errors.push('Only PDF files are allowed');
        }
        
        if (file.size > 10 * 1024 * 1024) { // 10MB
            errors.push('File size must not exceed 10MB');
        }
        
        // Note: Page count validation would need to be done on the server side
        // as it requires parsing the PDF, which is too expensive for client-side
        
        return errors;
    };

    const {
        getRootProps,
        getInputProps,
        isFocused,
        isDragAccept,
        isDragReject,
        fileRejections,
    } = useDropzone({
        multiple: true,
        maxFiles,
        accept: {
            'application/pdf': ['.pdf'],
        },
        maxSize: 10 * 1024 * 1024, // 10MB
        disabled,
        onDrop: (acceptedFiles: FileWithPath[]) => {
            // Validate each file and preserve File object properties
            const validFiles: File[] = [];
            
            acceptedFiles.forEach((file) => {
                const validationErrors = validatePDFFile(file);
                if (validationErrors.length === 0) {
                    // Only add valid files, preserve the original File object
                    validFiles.push(file);
                }
            });
            
            if (validFiles.length > 0) {
                onFilesSelected(validFiles);
            }
        },
    });

    const style: any = useMemo(() => ({
        ...baseStyle,
        ...(isFocused ? focusedStyle : {}),
        ...(isDragAccept ? acceptStyle : {}),
        ...(isDragReject ? rejectStyle : {}),
        ...(disabled ? { 
            opacity: 0.6, 
            cursor: 'not-allowed',
            transform: 'none',
            boxShadow: 'none',
        } : {}),
    }), [
        isFocused,
        isDragAccept,
        isDragReject,
        disabled,
    ]);

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / (k ** i)).toFixed(2))} ${sizes[i]}`;
    };

    const hasErrors = fileRejections.length > 0;

    return (
        <div className={cx('multi-file-pdf-dropzone')}>
            <div
                {...getRootProps({ style })}
                className={cx('dropzone', { 
                    'has-error': hasErrors,
                    disabled,
                    'drag-active': isDragAccept,
                    'drag-reject': isDragReject,
                })}
            >
                <input {...getInputProps()} />
                
                <div className={cx('upload-content')}>
                    <div className={cx('upload-icon')}>
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path 
                                d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z" 
                                fill="currentColor"
                                opacity="0.6"
                            />
                            <path 
                                d="M12 11V16L14 14M12 16L10 14" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                    
                    <div className={cx('upload-text')}>
                        <div className={cx('primary-text')}>
                            {isDragAccept ? 'Drop PDF files here' : 'Click or drag PDF files to upload'}
                        </div>
                        <div className={cx('secondary-text')}>
                            {helpText || `Upload up to ${maxFiles} PDF files, max ${formatFileSize(10 * 1024 * 1024)} each`}
                        </div>
                        <div className={cx('file-requirements')}>
                            <span className={cx('requirement')}>PDF format only • Maximum 10MB per file • Maximum 100 pages per file</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Error Messages */}
            {hasErrors && (
                <div className={cx('error-messages')}>
                    {fileRejections.map(({ file, errors }) => (
                        <div key={file.name} className={cx('error-item')}>
                            <span className={cx('file-name')}>
                                {file.name}
                                :
                            </span>
                            {errors.map((error) => (
                                <span key={error.code} className={cx('error-text')}>
                                    {error.message}
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

MultiFilePDFDropzone.propTypes = {
    onFilesSelected: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    maxFiles: PropTypes.number,
    helpText: PropTypes.string,
};

MultiFilePDFDropzone.defaultProps = {
    disabled: false,
    maxFiles: 10,
    helpText: undefined,
};

export default MultiFilePDFDropzone; 